import { locationIdFormat } from "@redotech/redo-model/ids";
import { plainDate } from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";

export const forecastFrequency = ["day", "week", "month"] as const;
export type ForecastFrequency = (typeof forecastFrequency)[number];

const ForecastDataPoint_ = z.object({
  date: plainDate(),
  // Prediction values (null if no forecast for this date)
  prediction: z.number().nullable(),
  lowerBound: z.number().nullable(),
  upperBound: z.number().nullable(),
  // Actual sales value (null if no historical data for this date)
  actual: z.number().nullable(),
  // For partial periods (current week/month): actual sales so far in this period
  // Used to show "Sales thus far: X" in tooltip for the current period
  actualSoFar: z.number().nullable().optional(),
  // Sum of manual forecast adjustments applied to this date (or aggregated
  // across the bucket for week/month views). Null when no underlying prediction
  // row exists (e.g. historical dates); 0 means "no adjustment for this bucket".
  adjustment: z.number().nullable().optional(),
});

export const ForecastDataPoint: z.ZodType<ForecastDataPoint> =
  ForecastDataPoint_;
export interface ForecastDataPoint extends z.infer<typeof ForecastDataPoint_> {}

const ForecastLocation_ = z.object({
  id: locationIdFormat.schema,
  name: z.string(),
});

export const ForecastLocation: z.ZodType<ForecastLocation> = ForecastLocation_;
export interface ForecastLocation extends z.infer<typeof ForecastLocation_> {}

const GetSkuSalesForecastOutput_ = z.object({
  sku: z.string(),
  data: z.array(ForecastDataPoint),
  forecastLocations: z.array(ForecastLocation).optional(),
});

export const GetSkuSalesForecastOutput: z.ZodType<GetSkuSalesForecastOutput> =
  GetSkuSalesForecastOutput_;
export interface GetSkuSalesForecastOutput
  extends z.infer<typeof GetSkuSalesForecastOutput_> {}

export const getSkuSalesForecast = {
  name: "getSkuSalesForecast" as const,
  input: z.object({
    sku: z.string(),
    startDate: plainDate(),
    endDate: plainDate(),
    frequency: createEnum(forecastFrequency).optional().default("day"),
    locationId: locationIdFormat.schema.optional(),
  }),
  output: GetSkuSalesForecastOutput,
};
